import React, { useContext, useEffect, useState } from 'react'
import { styled } from 'linaria/react'
import VbButton from '../../Core/SiteButton'
import { theme } from '../../Theme'
import { SiteContext } from '../../Global/SiteContext'
import Image from '@jetshop/ui/Image/Image'
import ChannelContext from '@jetshop/core/components/ChannelContext'
import PriceSection from '../../Core/Cards/PriceSection'
import { useScrollVisibility, useIsScrolledToBottom } from '../../Core/Hooks/useScrollVisibility'
import Link from '../../Core/Link'
import { useIntl } from '@jetshop/intl'
import { PRODUCT_ADD_TO_CART_MOBILE_ID } from '../../Core/Util/Constants'
import { getPackageStockStatus, isArrayWithItems } from '../../Core/Util/Helpers'
import { STOCK_ID } from '../../Core/Util/Constants'
import { useAddToCart } from '../../../hooks/useAddToCart'

const AddToCartFixedWrapper = styled.div`
   transition: opacity 0.15s ease, transform 0.15s ease;
   position: fixed;
   z-index: 100;
   bottom: 0;
   right: 0;
   left: 0;
   box-shadow: 0 -3px 10px 0 rgba(0, 0, 0, 0.1);
   width: 100%;

   border-radius: 32px 32px 0 0;
   background-color: white;

   &.show {
      opacity: 1;
      transform: translateY(0px);
   }

   &.hide {
      opacity: 0;
      transform: translateY(200px);
   }
`

const AddToCartFixedContainer = styled.div`
   .product-image-wrapper {
      flex-basis: 80px;
      max-height: auto;
   }
   .product-card-image {
      width: 100%;
      aspect-ratio: 1/1;
      object-fit: contain;
      padding: 0.75rem;
   }

   .product-card-info {
      padding-top: 0.3rem;
      padding-bottom: 1rem;
   }

   .product-artnr {
      font-size: 0.688rem;
      font-family: var(--font-industry);
      font-weight: 500;
   }

   .product-card-title {
      font-size: 0.95rem;
      font-weight: 700;
   }

   .product-card-title a {
      font-family: var(--font-industry);
      color: black;
   }

   .product-cart-button {
      width: 100%;
      max-width: 992px;
      padding: 0.5rem 0.75rem;
      ${theme.below.md} {
         font-size: 1rem;
      }
      ${theme.below.sm} {
         font-size: 0.8rem;
      }
   }

   .one-line-ellipsis {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      word-break: break-all;
      line-height: normal;
   }
`

const ProductAddToCartFixed = () => {
   const { selectedChannel } = useContext(ChannelContext)
   const {
      activeVariant,
      packageProducts,
   } = useContext(SiteContext)
   const { addProductToCart } = useAddToCart();
   const t = useIntl()
   const { isTargetVisible: isCartButtonVisible } = useScrollVisibility(
      PRODUCT_ADD_TO_CART_MOBILE_ID
   )
   const isScrolledToBottom = useIsScrolledToBottom()

   const {
      title,
      brandName,
      articleNumber,
      images,
      url,
      isPackage,
      stockStatus,
      currentPrice,
      onlyForSaleInStore
   } = activeVariant ?? {}
   
   const isMainVariantPackage = isPackage === true
   const isPackageItems = isMainVariantPackage && isArrayWithItems(packageProducts)
   const packageStatusInfo =
      isPackageItems && stockStatus?.buyable === true
         ? getPackageStockStatus(packageProducts)
         : {stockStatusId: STOCK_ID.OUT_OF_STOCK, text: t('stock status out of stock') }

   const stockStatusId = isMainVariantPackage ? packageStatusInfo?.stockStatusId : stockStatus?.stockStatusId

   const buttonText = () => {
      if (onlyForSaleInStore) {
         return t('only for sale in store')
      }
      if (STOCK_ID.IN_STOCK.includes(stockStatusId)) {
         return t('add to cart')
      } else if (STOCK_ID.PREBOOK.includes(stockStatusId)) {
         return t('book in advance')
      } else {
         return t('Out of stock')
      }
   }
   const buttonColor = () => {
      if (onlyForSaleInStore) {
         return 'black'
      }
      if (STOCK_ID.IN_STOCK.includes(stockStatusId)) { 
         return 'green'
      } else if (STOCK_ID.PREBOOK.includes(stockStatusId)) { 
         return 'yellow'
      } else {
         return 'black'
      }
   }

   const isButtonDisabled = () => {
      return (
         STOCK_ID.OUT_OF_STOCK.includes(stockStatusId) ||
         !currentPrice ||
         stockStatus?.buyable === false ||
         onlyForSaleInStore
      )
   }

   const handleAddToCart = () => {
      addProductToCart(activeVariant, articleNumber, 1, isPackageItems);
   }

   if (!activeVariant) {
      return <></>
   }

   return (
      <AddToCartFixedWrapper className={isCartButtonVisible || (!isCartButtonVisible && isScrolledToBottom) ? 'hide' : 'show'}>
         <AddToCartFixedContainer className="br-8 d-flex flex-row position-relative">
            <div className="product-image-wrapper">
               <Link to={url}>
                  <Image
                     className="product-card-image"
                     src={images[0]?.url}
                     aspect="1:1"
                  ></Image>
               </Link>
            </div>

            <div className="product-card-info d-flex flex-column px-8 flex-grow-1">
               {/* <div className="product-artnr mb-0">
                  ARTNR: {articleNumber}
               </div> */}
               <div className="product-card-title d-flex">
                  <Link to={url}>
                     <span className="one-line-ellipsis">
                        {brandName && <span>{brandName} </span>}
                        {title}
                     </span>
                  </Link>
               </div>

               <div className="row d-flex align-items-center mb-1">
                  <PriceSection
                     product={activeVariant}
                     selectedChannel={selectedChannel}
                     showVatIndicator={true}
                     overrideWrapperClass={' '}
                  ></PriceSection>
               </div>

               <VbButton
                  className="px-16 product-cart-button"
                  size="large"
                  styleType="solid"
                  uppercase
                  disabled={isButtonDisabled()}
                  color={buttonColor()}
                  onClick={handleAddToCart}
               >
                  {buttonText()}
               </VbButton>
            </div>
         </AddToCartFixedContainer>
      </AddToCartFixedWrapper>
   )
}

export default ProductAddToCartFixed
